import { openDB } from "idb";

const DB_NAME = "haccp-au";
const DB_VERSION = 1;

let _db = null;
async function getDB() {
  if (_db) return _db;
  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("outbox")) {
        const s = db.createObjectStore("outbox", { keyPath: "key" });
        s.createIndex("queued_at", "queued_at");
      }
      if (!db.objectStoreNames.contains("snapshots")) {
        db.createObjectStore("snapshots", { keyPath: "site_id" });
      }
      if (!db.objectStoreNames.contains("optimistic")) {
        // optimistic-only rows the UI displays before sync (keyed by record id)
        const s = db.createObjectStore("optimistic", { keyPath: "_key" });
        s.createIndex("site_resource", ["site_id", "resource"]);
      }
    },
  });
  return _db;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback: RFC4122 v4 lite
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export async function enqueue(item) {
  // item = { method, url, params, body, resource, site_id, optimistic? }
  const db = await getDB();
  const key = newId();
  const queued_at = Date.now();
  await db.put("outbox", { key, queued_at, status: "pending", attempts: 0, ...item });
  if (item.optimistic) {
    await db.put("optimistic", {
      _key: item.optimistic.id,
      ...item.optimistic,
      _pending: true,
      _queued_at: queued_at,
    });
  }
  return key;
}

export async function listOutbox() {
  const db = await getDB();
  return (await db.getAll("outbox")).sort((a, b) => a.queued_at - b.queued_at);
}

export async function pendingCount() {
  const db = await getDB();
  return (await db.count("outbox")) || 0;
}

export async function dequeue(key) {
  const db = await getDB();
  await db.delete("outbox", key);
}

export async function markAttempt(key, error) {
  const db = await getDB();
  const item = await db.get("outbox", key);
  if (!item) return;
  item.attempts = (item.attempts || 0) + 1;
  item.last_error = error || "";
  item.last_attempt_at = Date.now();
  await db.put("outbox", item);
}

export async function pruneStaleOutbox() {
  const db = await getDB();
  const cutoff = Date.now() - SEVEN_DAYS_MS;
  const all = await db.getAll("outbox");
  for (const it of all) {
    if (it.queued_at < cutoff) await db.delete("outbox", it.key);
  }
}

// ---- Snapshot ----
export async function saveSnapshot(site_id, data) {
  const db = await getDB();
  await db.put("snapshots", { site_id, data, saved_at: Date.now() });
}
export async function loadSnapshot(site_id) {
  const db = await getDB();
  const s = await db.get("snapshots", site_id);
  return s?.data || null;
}

// ---- Optimistic rows ----
export async function listOptimistic(site_id, resource) {
  const db = await getDB();
  const idx = db.transaction("optimistic").store.index("site_resource");
  const rows = await idx.getAll([site_id, resource]);
  return rows;
}
export async function clearOptimistic(id) {
  const db = await getDB();
  await db.delete("optimistic", id);
}
